import { useState, useCallback } from 'react';
import {
  Search as SearchIcon,
  Bell,
  Plus,
  Trash2,
  Loader2,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PaperSearch } from '@/features/search/components/PaperSearch';
import { SearchResults } from '@/features/search/components/SearchResults';
import {
  useSearchPapers,
  useImportPaper,
  useAlerts,
  useCreateAlert,
  useToggleAlert,
  useDeleteAlert,
} from '@/features/search/hooks/useSearch';
import type { SearchFilters, SemanticScholarPaper } from '@/features/search/hooks/useSearch';
import { formatDistanceToNow } from 'date-fns';

const RESULTS_PER_PAGE = 10;

export function Search() {
  const [filters, setFilters] = useState<SearchFilters | null>(null);
  const [offset, setOffset] = useState(0);
  const [showCreateAlert, setShowCreateAlert] = useState(false);
  const [alertQuery, setAlertQuery] = useState('');
  const [alertFrequency, setAlertFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');

  const currentFilters = filters
    ? { ...filters, offset, limit: RESULTS_PER_PAGE }
    : null;

  const { data, isLoading, isError, error } = useSearchPapers(currentFilters);
  const importPaper = useImportPaper();
  const { data: alerts, isLoading: alertsLoading } = useAlerts();
  const createAlert = useCreateAlert();
  const toggleAlert = useToggleAlert();
  const deleteAlert = useDeleteAlert();

  const handleSearch = useCallback((newFilters: SearchFilters) => {
    setOffset(0);
    setFilters(newFilters);
  }, []);

  const handleImport = useCallback(
    (paper: SemanticScholarPaper, folderId?: string) => {
      importPaper.mutate({ paper, folder_id: folderId });
    },
    [importPaper]
  );

  const handleCreateAlert = () => {
    if (!alertQuery.trim()) return;
    createAlert.mutate(
      { query: alertQuery.trim(), frequency: alertFrequency },
      { onSuccess: () => setShowCreateAlert(false) }
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Paper Search</h1>
        <p className="text-sm text-slate-500 mt-1">
          Search Semantic Scholar for academic papers and import them as sources.
        </p>
      </div>

      <Tabs defaultValue="search">
        <TabsList>
          <TabsTrigger value="search" className="gap-1.5">
            <SearchIcon className="h-4 w-4" />
            Search
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-1.5">
            <Bell className="h-4 w-4" />
            Alerts
            {alerts && alerts.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-[10px] px-1.5">
                {alerts.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="space-y-6 mt-4">
          <PaperSearch onSearch={handleSearch} isLoading={isLoading} />
          {filters && (
            <SearchResults
              data={data}
              isLoading={isLoading}
              isError={isError}
              errorMessage={error instanceof Error ? error.message : undefined}
              offset={offset}
              limit={RESULTS_PER_PAGE}
              onPageChange={setOffset}
              onImport={handleImport}
              isImporting={importPaper.isPending}
            />
          )}
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Get notified when new papers match your saved queries.
            </p>
            <Button size="sm" onClick={() => setShowCreateAlert(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New alert
            </Button>
          </div>

          {alertsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : !alerts || alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <Bell className="h-8 w-8 mb-3" />
              <p className="text-sm font-medium text-slate-700">No alerts yet</p>
              <p className="text-xs mt-1">Create an alert to get notified of new papers.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <Card key={alert.id}>
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">
                        {alert.query}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <Badge variant="outline" className="text-[10px]">
                          {alert.frequency}
                        </Badge>
                        {alert.last_checked && (
                          <span>
                            Last checked{' '}
                            {formatDistanceToNow(new Date(alert.last_checked), {
                              addSuffix: true,
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={alert.active}
                        onCheckedChange={(active) =>
                          toggleAlert.mutate({ id: alert.id, active })
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-rose-500"
                        onClick={() => deleteAlert.mutate(alert.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={showCreateAlert} onOpenChange={setShowCreateAlert}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create alert</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Search query</label>
              <Input
                placeholder="e.g. machine learning healthcare"
                value={alertQuery}
                onChange={(e) => setAlertQuery(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Frequency</label>
              <Select value={alertFrequency} onValueChange={(v) => setAlertFrequency(v as 'daily' | 'weekly' | 'monthly')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAlert(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateAlert}
              disabled={!alertQuery.trim() || createAlert.isPending}
            >
              {createAlert.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
